import { useEffect, useMemo, useState } from 'react';
import { ApiService } from '@core/services/api.service';
import { DataResponse } from '@core/@models/Types';
import { finalize } from 'rxjs/operators';
import { useTranslation } from 'react-i18next';
import { useSearchContext } from '@contexts/SearchProvider';

export interface FetchData<D extends DataResponse> {
  data: D[];
  loading: boolean;
  onSearch: <T extends DataResponse>(
    data: T[],
    fieldName: Array<keyof T>
  ) => T[];
  fetchData: () => void;
}

export const useDataFetching = <D extends DataResponse>(
  url: string
): FetchData<D> => {
  const { t } = useTranslation();
  const [data, setData] = useState<D[]>([]);
  const [loading, setLoading] = useState(false);
  const { queryParam, filter } = useSearchContext();

  const apiService = useMemo(() => new ApiService(url), []);

  useEffect(() => {
    fetchData();
  }, [queryParam, t]);

  const fetchData = () => {
    apiService
      .getData<D>(queryParam)
      .pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: (result: D[]) => {
          setLoading(true);
          setData([...result]);
        },
      });
  };

  const onSearch = <T extends DataResponse>(
    dataSearch: T[],
    fieldNames: Array<keyof T>
  ): T[] => {
    let result: T[] = dataSearch;
    const q = filter;
    if (q) {
      fieldNames.forEach((f) => {
        result = result.filter((d) => {
          const fieldValue: unknown = d[f];
          if (typeof fieldValue === 'string') {
            const searchField: string = fieldValue;
            return q[f]
              ? searchField.toLowerCase().indexOf(q[f].toLowerCase()) > -1
              : [];
          } else if (
            typeof fieldValue === 'object' &&
            Array.isArray(fieldValue)
          ) {
            const searchField: any = fieldValue;
            return q[f]
              ? searchField.filter((s: any) => s.key === q[f]).length > 0
              : [];
          } else if (typeof fieldValue === 'boolean') {
            return q[f] === undefined ? true : q[f] === fieldValue;
          } else {
            return q[f] === undefined || q[f] === '';
          }
        });
      });
    }
    return result;
  };
  return { data, loading, fetchData, onSearch };
};
